import React, { useEffect, useState } from 'react';
import styles from './CompanyDetails.module.scss';
import { Row, Upload, message } from 'antd';
import Logo from 'assets/logo/logo_full.png';
import { Formik, Form as FormikForm } from 'formik';
import InputField from 'shared/components/InputField';
import COButton from 'shared/components/COButton';
import COSelect from 'shared/components/COSelect';
import Camera from 'assets/icons/camera.svg';
import type { RcFile } from 'antd/es/upload';
import {
  CompanyDetailsFormMetas,
  initialValues,
  validationSchema,
} from './CompanyDetailsFormConstants';
import { ImageUploadProps } from 'enums/CompanyDetailsFormEnums';
import { CompanyDetailFormPropsEnum } from 'enums/CompanyDetailsFormEnums';
import ErrorMessage from 'shared/components/Error';
import { getCompanyDetailsFields } from './CompanyDetailsFormFields';
import {
  convertToOptions,
  convertToOptionsCompanyType,
  CategoryOptions,
  CategoryOptionsCompanyType,
} from './CompanyDetailsFormConstants';
import UserCompanyService from 'services/AuthenticationService/userCompany.service';
import EmissionService from 'services/ProductsService/emission.service';

const { REFERRAL, REVENUE_RANGE, ACCOUNT_COMPANY_TYPE } =
  CompanyDetailsFormMetas;

interface CompanyDetailsFormValues {
  companyName: string;
  companyType: string | undefined;
  revenuerange: string | undefined;
  source: string | undefined;
}

const CompanyDetailsForm: React.FC = () => {
  const { getCompanyDetails, buttonLoading } = UserCompanyService();
  const {
    fetchMetaData,
    metaData,
    referralData,
    fetchReferralData,
    fetchCompanyTypeMeta,
    companyTypeMeta,
  } = EmissionService();

  const [revenueRange, setRevenueRange] = useState<CategoryOptions[]>();
  const [referenceData, setReferenceData] = useState<CategoryOptions[]>();
  const [companyMeta, setCompanyMeta] =
    useState<CategoryOptionsCompanyType[]>();
  const [image, setImage] = useState<RcFile>();

  useEffect(() => {
    fetchMetaData({ metadata_type: REVENUE_RANGE });
    fetchReferralData({ metadata_type: REFERRAL });
    fetchCompanyTypeMeta({ metadata_type: ACCOUNT_COMPANY_TYPE });
  }, []);

  useEffect(() => {
    const revenueRange = convertToOptions(metaData);
    const referenceData = convertToOptions(referralData);
    const companyType = convertToOptionsCompanyType(companyTypeMeta);

    setRevenueRange(revenueRange);
    setReferenceData(referenceData);
    setCompanyMeta(companyType);
  }, [metaData, referralData, companyTypeMeta]);

  const Fields = getCompanyDetailsFields({
    revenueRange,
    companyMeta,
    referenceData,
  });

  // Verifying the image before upload - checking the mime type of an image
  const beforeUpload = (file: RcFile) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(ImageUploadProps.ERROR_MESSAGE_SIZE);
      return Upload.LIST_IGNORE;
    }
    setImage(file);
    return false;
  };

  const handleApiSubmission = async (values: CompanyDetailsFormValues) => {
    getCompanyDetails(
      {
        name: values.companyName,
        type: values.companyType,
        revenue_range: values.revenuerange,
        member: '',
        referral: values.source,
      },
      image
    );
  };

  const uploadButton = (
    <>
      <div className={styles.uploadIcon}>
        <img src={Camera} alt={ImageUploadProps.ICON_ALT} />
      </div>
    </>
  );

  return (
    <div className={styles.formContainer}>
      <Row className={styles.logoContainer}>
        <img
          src={Logo}
          alt={CompanyDetailFormPropsEnum.IMAGE_ALT}
          className={styles.logo}
        />
      </Row>
      <Row className={styles.headingContainer}>
        <div>
          <h1>{CompanyDetailFormPropsEnum.PAGE_TITLE}</h1>
        </div>
      </Row>
      <div className={styles.fieldsContainer}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={values => handleApiSubmission(values)}>
          {formik => (
            <FormikForm>
              <div className={styles.uploadContainer}>
                <div className={styles.uploadWrapper}>
                  <Upload
                    name={ImageUploadProps.NAME}
                    listType={ImageUploadProps.LIST_TYPE}
                    showUploadList={{
                      showDownloadIcon: false,
                      showPreviewIcon: false,
                    }}
                    beforeUpload={beforeUpload}
                    accept="image/png, image/jpeg"
                    maxCount={1}
                    customRequest={() => {}}
                    onRemove={() => setImage(undefined)}>
                    {!image && uploadButton}
                  </Upload>
                  <ErrorMessage message={formik?.errors?.image} />
                </div>
              </div>
              {Fields.map((field, index) => {
                return (
                  <div key={index} className={styles.fieldContainer}>
                    {field.select ? (
                      <COSelect
                        placeholderstring={field.placeholder}
                        name={field.name}
                        options={field.selectOptions}
                        label={field.label}
                      />
                    ) : (
                      <InputField
                        name={field.name}
                        placeholderstring={field.placeholder}
                        label={field.label}
                      />
                    )}
                  </div>
                );
              })}
              <div className={styles.buttonContainer}>
                <COButton
                  loading={buttonLoading}
                  submit
                  disabled={!formik.isValid || !image}>
                  {CompanyDetailFormPropsEnum.BUTTON_LABEL_SUBMIT}
                </COButton>
              </div>
            </FormikForm>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CompanyDetailsForm;
